import { Component, Input, ViewChild } from '@angular/core';

import { ApiError, ButtonLoader, MessageService } from '../../../core';
import { PessoaContatoFormComponent } from './form';
import { PessoaContato } from './pessoa-contato';
import { contatoSituacaoAtivo, contatoSituacaoInativo, PessoaContatoUpdateSituacao } from './situacao';
import { PessoaContatoTableComponent } from './table';
import { PessoaContatoTipo, PessoaContatoTipoSortPipe, outrosContatosTipos } from './tipo';
import { PessoaContatoHttpService } from './services';
import { take } from 'rxjs/operators';

@Component({
    selector: 'sgt-pessoa-contato',
    templateUrl: './pessoa-contato.component.html',
    styleUrls: ['./pessoa-contato.component.less'],
    providers: [
        PessoaContatoTipoSortPipe
    ]
})
export class PessoaContatoComponent {
    @Input() canEdit = true;
    @Input() title = 'Contatos';
    @Input() idPessoa: number;
    @Input() entities: PessoaContato[] = [];
    @Input() persistSaves = false;

    // Table
    @Input() idTable: string = null;
    @Input() showTableTitle = true;
    @Input() tableMaxHeight = 'none';
    @ViewChild('table') table: PessoaContatoTableComponent;

    // Form
    isFormOpen: boolean;
    isFormEdit: boolean;
    entityIndexEditing: number;
    @ViewChild('form') form: PessoaContatoFormComponent;
    tiposContatos: PessoaContatoTipo[];

    constructor(private messageService: MessageService,
                private pessoaContatoTipoSortPipe: PessoaContatoTipoSortPipe,
                private pessoaContatoHttpService: PessoaContatoHttpService) { }

    ngOnInit(): void {
        this.tiposContatos = this.pessoaContatoTipoSortPipe.transform(outrosContatosTipos);
    }

    insert(tipoContato: PessoaContatoTipo) {
        this.isFormOpen = true;
        this.isFormEdit = false;
        this.entityIndexEditing = null;
        setTimeout(() => this.form.insert(tipoContato.id), 0);
    }

    edit(index: number) {
        if (index == null || index === undefined || index < 0) {
            return;
        }

        this.isFormOpen = true;
        this.isFormEdit = true;
        this.entityIndexEditing = index;
        setTimeout(() => this.form.edit(this.entities[index]), 0);
    }

    delete(index: number) {
        if (index == null || index === undefined || index < 0) {
            return;
        }

        const entity = this.entities[index];
        this.messageService.confirm(`Deseja realmente excluir o contato ${entity.nome}?`).then(result => {
            if (result) {
                this.entities.splice(index, 1);
                this.table.refresh();
            }
        });
    }

    /** Persiste e insere o novo contato no banco de dados */
    private persistInsert(entity: PessoaContato): Promise<PessoaContato> {
        return new Promise<PessoaContato>((resolve, reject) => {
            this.pessoaContatoHttpService.insert(this.idPessoa, entity)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Persiste e insere o novo contato no banco de dados */
    private persistUpdate(entity: PessoaContato): Promise<PessoaContato> {
        return new Promise<PessoaContato>((resolve, reject) => {
            this.pessoaContatoHttpService.update(entity)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Persite e atualiza a situação do contato no banco de dados */
    private persistUpdateSituacao(id: number, situacao: number): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const updateSituacao = new PessoaContatoUpdateSituacao(id, situacao);
            this.pessoaContatoHttpService.updateSituacao(updateSituacao)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => resolve(response),
                    (error: ApiError) => reject(error)
                );
        });
    }

    async save() {
        if (!this.form.isValid()) {
            this.messageService.info('Para salvar o contato é preciso informar corretamente todos os campos destacados em vermelho', 'Informação');
            return;
        }

        try {
            const btnSave = new ButtonLoader('#btnSaveContato');
            btnSave.loading('Salvando');

            let entity = this.form.entity;

            if (this.isFormEdit) { // Edit
                // Se for para persistir e atualizar no banco de dados
                if (this.persistSaves) {
                    entity = await this.persistUpdate(entity);
                }

                this.entities[this.entityIndexEditing] = entity;
            } else { // Insert
                // Se for para persistir e inserir no banco de dados
                if (this.persistSaves) {
                    entity = await this.persistInsert(entity);
                }

                this.entities.push(entity);
            }

            btnSave.reset();
            this.afterSave();
        } catch (error) {
            this.messageService.error('Ocorreu um erro ao salvar o contato. Tente novamente', 'Erro', error);
        }
    }

    /** Ativa o contato */
    async ativar(index: number) {
        try {
            if (index == null || index === undefined || index < 0) {
                return;
            }
            const contato = this.entities[index];
            const situacao = contatoSituacaoAtivo.id

            // Se for para persistir a atualização no banco de dados
            if (this.persistSaves && contato.id > 0) {
                await this.persistUpdateSituacao(contato.id, situacao);
            }

            contato.situacao = situacao;
        } catch (error) {
            this.messageService.error('Ocorreu um erro ao atualizar a situação do contato. Tente novamente', 'Erro', error);
        }
    }

    /** Inativa o contato */
    async inativar(index: number) {
        try {
            if (index == null || index === undefined || index < 0) {
                return;
            }
            const contato = this.entities[index];
            const situacao = contatoSituacaoInativo.id;

            // Se for para persistir a atualização no banco de dados
            if (this.persistSaves && contato.id > 0) {
                await this.persistUpdateSituacao(contato.id, situacao);
            }

            contato.situacao = situacao;
        } catch (error) {
            this.messageService.error('Ocorreu um erro ao atualizar a situação do contato. Tente novamente', 'Erro', error);
        }
    }

    /** Seta o foco no form do contato */
    setFocusForm() {
        this.form?.setFocus();
    }

    private closeForm() {
        this.isFormOpen = false;
        this.isFormEdit = false;
        this.entityIndexEditing = null;
    }

    private afterSave() {
        this.closeForm();
    }

    cancel() {
        this.closeForm();
    }
}
